if (!customElements.get('fotra-gallery')) {
  customElements.define('fotra-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.face = this.face || 'front';
      this.original = Boolean(this.original);
      this.sequence = this.sequence || 0;
      this.image = this.querySelector('[data-gallery-image]');
      this.dialog = this.querySelector('dialog');
      this.opener = this.querySelector('.fotra-gallery-open');
      if (!this.image || !this.dialog || !this.opener) return;
      this.querySelectorAll('[data-enhanced]').forEach(element => { element.hidden = false; });
      this.querySelectorAll('[data-face]').forEach(button => button.addEventListener('click', () => this.loadView(button.dataset.face, this.original), options));
      this.querySelectorAll('[data-original]').forEach(button => button.addEventListener('click', () => this.loadView(this.face, !this.original), options));
      this.querySelectorAll('[data-retry]').forEach(button => button.addEventListener('click', () => {
        const view = this.retryView || {face: this.face, original: this.original};
        this.loadView(view.face, view.original);
      }, options));
      this.opener.addEventListener('click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0 || typeof this.dialog.showModal !== 'function') return;
        event.preventDefault();
        this.syncDialog();
        this.resetZoom();
        this.dialog.showModal();
      }, options);
      this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => this.opener.focus(), options);
      this.querySelector('[data-zoom]').addEventListener('click', event => {
        const zoomed = this.dialog.classList.toggle('is-zoomed');
        event.currentTarget.textContent = zoomed ? 'Fit image' : 'Zoom in';
        event.currentTarget.setAttribute('aria-pressed', String(zoomed));
      }, options);
      const initialFailure = () => {
        this.retryView = {face: this.face, original: this.original};
        this.status('This image could not load. Retry or open the full image.', true);
      };
      this.image.addEventListener('error', initialFailure, options);
      if (this.image.complete && this.image.naturalWidth === 0) initialFailure();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.sequence++;
      this.cancelPending?.();
    }
    status(message, retry = false) {
      this.querySelectorAll('[data-status]').forEach(element => { element.textContent = message; });
      this.querySelectorAll('[data-retry]').forEach(button => { button.hidden = !retry; });
    }
    resetZoom() {
      this.dialog.classList.remove('is-zoomed');
      const button = this.querySelector('[data-zoom]');
      button.textContent = 'Zoom in';
      button.setAttribute('aria-pressed', 'false');
      this.querySelector('.fotra-dialog-image').scrollTo(0, 0);
    }
    syncDialog() {
      this.dataset.currentFace = this.face;
      const image = this.dialog.querySelector('img');
      image.src = this.image.src;
      image.alt = this.image.alt;
      image.setAttribute('width', this.image.getAttribute('width'));
      image.setAttribute('height', this.image.getAttribute('height'));
      this.querySelector('[data-source-link]').href = this.image.src;
    }
    async loadView(face, original) {
      const key = original ? (face === 'front' ? 'originalFront' : 'originalBack') : face;
      const source = this.image.dataset[key];
      if (!source) return;
      const request = ++this.sequence;
      this.cancelPending?.();
      const faceLabel = face === 'front' ? 'Front' : 'Back';
      this.status('Loading ' + faceLabel.toLowerCase() + (original ? ' sample photograph…' : ' image…'));
      this.querySelector('.fotra-dialog-image').setAttribute('aria-busy', 'true');
      this.opener.setAttribute('aria-busy', 'true');
      try {
        const candidate = await new Promise((resolve, reject) => {
          const image = new Image();
          let timer;
          const cleanup = () => { clearTimeout(timer); image.onload = null; image.onerror = null; };
          this.cancelPending = () => { cleanup(); reject(new Error('Image request replaced')); };
          image.onload = () => { cleanup(); resolve(image); };
          image.onerror = () => { cleanup(); reject(new Error('Image unavailable')); };
          timer = setTimeout(() => { cleanup(); reject(new Error('Image timed out')); }, 15000);
          image.src = source;
        });
        if (request !== this.sequence || !this.isConnected) return;
        this.face = face;
        this.original = original;
        this.retryView = null;
        const provenance = this.querySelector('[data-provenance]');
        const label = original ? 'Physical sample photograph' : provenance.dataset.styledLabel;
        this.image.src = candidate.src;
        this.image.setAttribute('width', String(candidate.naturalWidth));
        this.image.setAttribute('height', String(candidate.naturalHeight));
        this.image.alt = this.dataset.title + ' — ' + faceLabel.toLowerCase() + ', ' + label.toLowerCase();
        this.opener.href = candidate.src;
        provenance.textContent = label;
        this.querySelector('[data-face-label]').textContent = faceLabel;
        this.querySelectorAll('[data-face]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.face === face)));
        this.querySelectorAll('[data-original]').forEach(button => {
          button.setAttribute('aria-pressed', String(original));
          button.textContent = original ? 'View studio images' : 'Compare original sample';
        });
        this.syncDialog();
        this.resetZoom();
        this.status(faceLabel + ' · ' + label);
      } catch (error) {
        if (request !== this.sequence || !this.isConnected) return;
        this.retryView = {face, original};
        this.status('That image could not load. Your previous view is still shown. Retry when your connection is ready.', true);
      } finally {
        if (request === this.sequence) {
          this.cancelPending = null;
          this.opener.removeAttribute('aria-busy');
          this.querySelector('.fotra-dialog-image').removeAttribute('aria-busy');
        }
      }
    }
  });
}
